import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import "../../../styles/kanbans/board-holder.css";
import Swimlane, { type Lane, type JobCard } from "./Swimlane";
import JobKanbanCard from "./JobKanbanCard";
import DeleteJobModal from "./modals/DeleteJobModal";
import EditJobModal from "./modals/EditJobModal";
import { api } from "../../../utils/api";
import { API_ENDPOINTS } from "../../../config";
import { emitSubscriptionRefresh } from "../../../utils/subscriptionEvents";
import { showToast } from "../../../utils/toastManager";

type Props = {
  headerOffset?: number;
  onOpenJobModal?: (defaultList?: string) => void;
  initialJobId?: string;
  boardId?: string;
  defaultJobModalTab?: 'edit' | 'ai-tools' | 'activities' | 'notes' | 'contacts' | 'documents';
};

interface SwimlaneFromAPI {
  uid: string;
  name: string | null;
  position?: number;
}

interface KanbanFromAPI {
  uid: string;
  name: string;
  status: string;
  swimlanes?: SwimlaneFromAPI[];
}

interface JobFromAPI {
  uid: string;
  job_title: string;
  company_name: string;
  swimlane_uid: string;
  created_at?: string;
  updated_at?: string;
  color?: string | null;
  company_url?: string | null;
}

const getSwimlaneIcon = (name: string | null): string => {
  const upper = (name || "").toUpperCase();

  if (upper.includes("WISH")) return "wishlist";
  if (upper.includes("APPLY")) return "applied";
  if (upper.includes("INTERVIEW")) return "interview";
  if (upper.includes("OFFER")) return "offer";
  if (upper.includes("REJECT")) return "rejected";
  return "target";
};

const formatAge = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffDays > 0) return `${diffDays}d`;
  if (diffHours > 0) return `${diffHours}h`;
  if (diffMins > 0) return `${diffMins}m`;
  return "now";
};

export default function BoardHolder({ headerOffset = 180, onOpenJobModal, initialJobId, boardId, defaultJobModalTab }: Props) {
  const navigate = useNavigate();
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<{ cardId: string; laneId: string } | null>(null);
  const [laneToDelete, setLaneToDelete] = useState<string | null>(null);
  const [editJobModalOpen, setEditJobModalOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<JobCard | null>(null);

  // Mouse drags start after a small deliberate movement so plain clicks still
  // open the job. Touch drags need a long-press instead, otherwise the gesture
  // would swallow the board's horizontal scrolling.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    let cancelled = false;
    const fetchBoardData = async () => {
      if (!boardId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const kanbanData = await api.get<KanbanFromAPI>(API_ENDPOINTS.kanbans.get(boardId));
        if (cancelled) return;

        const jobsData = await api.get<JobFromAPI[]>(API_ENDPOINTS.jobs.listByKanban(boardId));
        if (cancelled) return;
        
        const swimlanes = kanbanData.swimlanes || [];
        
        const sortedSwimlanes = [...swimlanes].sort((a, b) => {
          const posA = a.position ?? Infinity;
          const posB = b.position ?? Infinity;
          return posA - posB;
        });
        
        const jobsBySwimlane = new Map<string, JobFromAPI[]>();
        jobsData.forEach(job => {
          const existing = jobsBySwimlane.get(job.swimlane_uid) || [];
          jobsBySwimlane.set(job.swimlane_uid, [...existing, job]);
        });
        
        const transformedLanes: Lane[] = sortedSwimlanes.map(swimlane => {
          const jobs = jobsBySwimlane.get(swimlane.uid) || [];
          const cards: JobCard[] = jobs.map(job => ({
            id: job.uid,
            title: job.job_title,
            company: job.company_name,
            age: formatAge(job.created_at || job.updated_at),
            accent: job.color || undefined,
          }));
          
          return {
            id: swimlane.uid,
            title: swimlane.name || "Unnamed",
            icon: getSwimlaneIcon(swimlane.name),
            jobs: cards.length,
            cards,
          };
        });
        
        if (cancelled) return;
        setLanes(transformedLanes);
      } catch (error) {
        if (cancelled) return;
        setLanes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchBoardData();
    return () => { cancelled = true; };
  }, [boardId]);

  useEffect(() => {
    if (initialJobId) {
      setSelectedJobId(initialJobId);
      setEditJobModalOpen(true);
    }
  }, [initialJobId]);

  const handleAddCardClick = (laneId: string) => {
    const lane = lanes.find(l => l.id === laneId);
    if (lane && onOpenJobModal) {
      onOpenJobModal(lane.title);
    }
  };

  const handleDeleteCardClick = (cardId: string, laneId: string) => {
    setCardToDelete({ cardId, laneId });
    setDeleteModalOpen(true);
  };

  const handleCardClick = (cardId: string) => {
    setSelectedJobId(cardId);
    setEditJobModalOpen(true);
  };

  const getSelectedJobDetails = () => {
    if (!selectedJobId) return { jobTitle: '', companyName: '', currentLaneId: '' };
    
    for (const lane of lanes) {
      const card = lane.cards?.find(c => c.id === selectedJobId);
      if (card) {
        return {
          jobTitle: card.title,
          companyName: card.company,
          currentLaneId: lane.id,
        };
      }
    }
    
    return { jobTitle: '', companyName: '', currentLaneId: '' };
  };

  const jobDetails = getSelectedJobDetails();

  const handleMoveJob = (toLaneId: string) => {
    if (!selectedJobId || !jobDetails.currentLaneId) return;
    
    handleCardMove(selectedJobId, jobDetails.currentLaneId, toLaneId);
  };

  const handleJobColorChange = (jobId: string, color: string | null) => {
    setLanes((prev) =>
      prev.map((lane) => ({
        ...lane,
        cards: lane.cards?.map((card) =>
          card.id === jobId ? { ...card, accent: color || undefined } : card
        ),
      }))
    );
  };

  const handleCloseEditModal = () => {
    setEditJobModalOpen(false);
    setSelectedJobId(null);
    if (boardId) {
      navigate(`/track/board/${boardId}`, { replace: true });
    }
  };

  const handleConfirmDelete = async () => {
    if (cardToDelete) {
      try {
        await api.delete(API_ENDPOINTS.jobs.delete(cardToDelete.cardId));

        setLanes((prev) =>
          prev.map((lane) => {
            if (lane.id === cardToDelete.laneId) {
              return {
                ...lane,
                cards: lane.cards?.filter((c) => c.id !== cardToDelete.cardId) || [],
                jobs: Math.max(0, lane.jobs - 1),
              };
            }
            return lane;
          })
        );

        emitSubscriptionRefresh();
      } catch (error: any) {
        if (error?.status === 404) {
          setLanes((prev) =>
            prev.map((lane) => {
              if (lane.id === cardToDelete.laneId) {
                return {
                  ...lane,
                  cards: lane.cards?.filter((c) => c.id !== cardToDelete.cardId) || [],
                  jobs: Math.max(0, lane.jobs - 1),
                };
              }
              return lane;
            })
          );
          emitSubscriptionRefresh();
        } else {
          showToast('Failed to delete job. Please try again.', 'error');
        }
      }
      
      setDeleteModalOpen(false);
      setCardToDelete(null);
      return;
    }

    if (laneToDelete) {
      if (!boardId) {

        setDeleteModalOpen(false);
        setLaneToDelete(null);
        return;
      }

      try {
        await api.delete(API_ENDPOINTS.kanbans.deleteSwimlane(laneToDelete, boardId));

        setLanes((prev) => prev.filter((l) => l.id !== laneToDelete));
      } catch (error: any) {

        
        if (error?.status === 422) {

        } else if (error?.status === 401) {

        } else if (error?.status === 404) {

          setLanes((prev) => prev.filter((l) => l.id !== laneToDelete));
        } else {

        }
      }
    }

    setDeleteModalOpen(false);
    setCardToDelete(null);
    setLaneToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
    setCardToDelete(null);
    setLaneToDelete(null);
  };

  const getDeleteModalProps = () => {
    if (laneToDelete) {
      const lane = lanes.find(l => l.id === laneToDelete);
      return {
        title: "Delete List",
        message: `Are you sure you want to delete the "${lane?.title}" list? All jobs in this list will be permanently deleted.`,
        confirmText: "Delete List",
      };
    }
    
    if (cardToDelete) {
      return {
        title: "Delete Job",
        message: "Are you sure you want to delete this job? This will also delete any job tailored resume and application packet associated with it.",
        confirmText: "Delete Job",
      };
    }

    return {
      title: "Delete",
      message: "Are you sure you want to delete this item?",
      confirmText: "Delete",
    };
  };

  const deleteModalProps = getDeleteModalProps();

  const handleRename = async (id: string, nextTitle: string) => {
    if (!boardId) {

      return;
    }

    const trimmedTitle = nextTitle.trim();
    if (!trimmedTitle) {

      return;
    }

    try {
      await api.post(API_ENDPOINTS.kanbans.changeSwimlaneName, {
        kanban_uid: boardId,
        swimlane_uid: id,
        name: trimmedTitle,
      });

      setLanes((prev) =>
        prev.map((l) => {
          if (l.id === id) {
            return {
              ...l,
              title: trimmedTitle,
              icon: getSwimlaneIcon(trimmedTitle),
            };
          }
          return l;
        })
      );
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
      
    }
  };

  const handleDelete = (id: string) => {
    setLaneToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleMove = (_id: string) => {
  };

  const handleAddLane = async () => {
    if (!boardId) {

      return;
    }

    const defaultName = "New List";

    try {
      const newSwimlane = await api.post<SwimlaneFromAPI>(API_ENDPOINTS.kanbans.createSwimlane, {
        kanban_uid: boardId,
        name: defaultName,
      });

      const newLane: Lane = {
        id: newSwimlane.uid,
        title: newSwimlane.name || defaultName,
        icon: getSwimlaneIcon(newSwimlane.name),
        jobs: 0,
        cards: [],
      };

      setLanes((prev) => [...prev, newLane]);
    } catch (error: any) {

      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
    }
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const cardId = String(event.active.id);
    for (const lane of lanes) {
      const card = lane.cards?.find((c) => c.id === cardId);
      if (card) {
        setActiveCard(card);
        return;
      }
    }
  }, [lanes]);

  const handleDragCancel = useCallback(() => setActiveCard(null), []);

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCard(null);

    const { active, over } = event;
    if (!over) return;

    const cardId = String(active.id);
    const fromLaneId = (active.data.current as { laneId?: string } | undefined)?.laneId;
    const toLaneId = String(over.id);

    if (!fromLaneId || fromLaneId === toLaneId) return;

    handleCardMove(cardId, fromLaneId, toLaneId);
  };

  const handleCardMove = async (cardId: string, fromLaneId: string, toLaneId: string) => {
    if (fromLaneId === toLaneId) {
      return;
    }

    let previousLanes: Lane[] = [];
    let cardToMove: JobCard | undefined;

    setLanes((prev) => {
      previousLanes = JSON.parse(JSON.stringify(prev));
      
      const fromLane = prev.find(l => l.id === fromLaneId);
      cardToMove = fromLane?.cards?.find(c => c.id === cardId);
      
      if (!cardToMove) {

        return prev;
      }

      return prev.map(lane => {
        if (lane.id === fromLaneId) {
          return {
            ...lane,
            cards: lane.cards?.filter(c => c.id !== cardId) || [],
            jobs: Math.max(0, lane.jobs - 1)
          };
        } else if (lane.id === toLaneId) {
          return {
            ...lane,
            cards: [...(lane.cards || []), cardToMove!],
            jobs: lane.jobs + 1
          };
        }
        return lane;
      });
    });

    try {
      await api.put(API_ENDPOINTS.jobs.updateSwimlane(cardId, toLaneId));
    } catch (error: any) {

      
      if (previousLanes.length > 0) {
        setLanes(previousLanes);
      }
      
      if (error?.status === 422) {

      } else if (error?.status === 401) {

      } else if (error?.status === 404) {

      } else {

      }
    }
  };

  if (loading) {
    return (
      <section className="boardHolder">
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 20px', 
          color: '#6b7280', 
          fontSize: '14px' 
        }}>
          Loading board...
        </div>
      </section>
    );
  }

  return (
    <section className="boardHolder">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="boardHolder__row" role="list">
          {lanes.map((lane) => (
            <Swimlane
              key={lane.id}
              lane={lane}
              headerOffset={headerOffset}
              onRename={handleRename}
              onMove={handleMove}
              onDelete={handleDelete}
              onAddCard={() => handleAddCardClick(lane.id)}
              onDeleteCard={(cardId) => handleDeleteCardClick(cardId, lane.id)}
              onCardClick={handleCardClick}
            />
          ))}

          <button 
            type="button" 
            className="boardHolder__addList" 
            onClick={handleAddLane}
          >
            + ADD LIST
          </button>
        </div>

        <DragOverlay dropAnimation={null}>
          {activeCard ? (
            <JobKanbanCard
              title={activeCard.title}
              company={activeCard.company}
              age={activeCard.age}
              logoUrl={activeCard.logoUrl}
              accent={activeCard.accent}
              className="jkCard--overlay"
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <DeleteJobModal
        open={deleteModalOpen}
        title={deleteModalProps.title}
        message={deleteModalProps.message}
        confirmText={deleteModalProps.confirmText}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <EditJobModal
        open={editJobModalOpen}
        onClose={handleCloseEditModal}
        jobId={selectedJobId || undefined}
        boardId={boardId || "job-search-2025"}
        jobTitle={jobDetails.jobTitle}
        companyName={jobDetails.companyName}
        currentLane={jobDetails.currentLaneId}
        availableLanes={lanes.map(lane => ({
          id: lane.id,
          title: lane.title,
          icon: lane.icon
        }))}
        onMoveJob={handleMoveJob}
        onColorChange={handleJobColorChange}
        defaultLeftTab={defaultJobModalTab}
      />
    </section>
  );
}